import { useRef, useState } from "react";
import { BATCH, DIAL_CODES, defaultDial, pickPeople, readSpreadsheet, type Person } from "../../../shared/clientFile";
import { useNative } from "../../../shared/phone/media";
import { Sheet as PhoneSheet } from "../../../shared/phone/Sheet";
import { ApiError } from "../../lib/api";
import { listsApi, type ListView, type MadeList } from "../../lib/listsApi";
import { Icon } from "../ui/Icon";
import { Sheet } from "../ui/Sheet";

/**
 * Adding a client list (notes/AUDIENCE.md 5.5, 9.1): the owner ticks that these are their own clients,
 * picks a spreadsheet, and the browser keeps only its emails and phone numbers, cleans them, and sends
 * them in batches. The same sheet adds more people to a list, or replaces it with a new one.
 */

export type AddMode = { kind: "new" } | { kind: "more"; list: ListView } | { kind: "replace"; list: ListView };

const message = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Something went wrong. Try again.");
const UNSURE = "Meta gave an unclear answer. Flashadz looks again in 10 minutes; then choose the file again.";

export function AddList({ productId, productName, mode, onClose, onDone }: { productId: string; productName: string; mode: AddMode | null; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<{ rows: string[][]; missing: number } | null>(null);
  const [dial, setDial] = useState(() => defaultDial(typeof navigator === "undefined" ? undefined : navigator.language));
  const file = useRef<HTMLInputElement>(null);
  const native = useNative();

  const close = () => {
    setName("");
    setConsent(false);
    setError(null);
    setPending(null);
    onClose();
  };

  /** Make (or reuse) the list on Meta, send every batch, and mark it done. */
  const send = async (people: Person[]) => {
    let list: MadeList | ListView;
    if (mode?.kind === "more") list = mode.list;
    else {
      const made = mode?.kind === "replace" ? await listsApi.replace(productId, mode.list.id) : await listsApi.add(productId, name.trim());
      list = made.list;
      if (made.list.state === "unsure") throw new Error(UNSURE);
      if (made.list.state !== "uploading") throw new Error(made.list.note ?? "Meta did not make the list. Try again.");
    }
    for (let i = 0; i < people.length; i += BATCH) await listsApi.people(productId, list.id, people.slice(i, i + BATCH));
    await listsApi.done(productId, list.id);
  };

  const go = async (rows: string[][], code: string | null) => {
    setBusy(true);
    setError(null);
    try {
      const picked = pickPeople(rows, code);
      if (picked.noColumns) throw new Error("The file has no email or phone column.");
      if (picked.needsCode && !code) return setPending({ rows, missing: picked.needsCode });
      if (!picked.people.length) throw new Error("The file has nobody with an email or a phone number.");
      await send(picked.people);
      onDone();
      close();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    setError(null);
    try {
      await go(await readSpreadsheet(f.name, await f.arrayBuffer()), null);
    } catch (e) {
      setError(message(e));
    }
  };

  const title = mode?.kind === "more" ? "Add more people" : mode?.kind === "replace" ? "Replace this list" : "Add a client list";
  const ready = consent && (mode?.kind !== "new" || name.trim().length > 0);
  const picker = (
    <input
      ref={file}
      type="file"
      hidden
      accept=".csv,.xlsx,.tsv,.txt,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      aria-label="The spreadsheet"
      onChange={(e) => {
        void onFile(e.target.files?.[0]);
        e.target.value = "";
      }}
    />
  );

  if (native)
    return (
      <PhoneSheet
        open={!!mode}
        onClose={close}
        title={title}
        className="pd-addlist"
        left={
          <button type="button" className="pn-tbtn" onClick={close}>
            Cancel
          </button>
        }
        foot={
          pending ? (
            <button className="pn-btn pri wide" type="button" disabled={busy} onClick={() => void go(pending.rows, dial)}>
              {busy && <span className="pd-spin" />}
              {busy ? "Sending to Meta" : "Continue"}
            </button>
          ) : (
            <button className="pn-btn pri wide" type="button" disabled={!ready || busy} onClick={() => file.current?.click()}>
              {busy ? <span className="pd-spin" /> : <Icon name="upload" />}
              {busy ? "Sending to Meta" : "Choose the file"}
            </button>
          )
        }
      >
        <p className="pd-ssub">
          {mode && mode.kind !== "new" ? `${mode.list.name}. ` : ""}A spreadsheet with an email or a phone number for each person.
        </p>
        {mode?.kind === "new" && (
          <div className="pn-grp">
            <div className="pn-irow col">
              <label htmlFor="pd-list-name">Name it</label>
              <input id="pd-list-name" value={name} maxLength={80} autoComplete="off" onChange={(e) => setName(e.target.value)} placeholder="Past clients" />
            </div>
          </div>
        )}
        <div className="pn-grp pd-gap">
          <button className="pn-row wrap pd-consent" type="button" role="checkbox" aria-checked={consent} onClick={() => setConsent((c) => !c)}>
            <span className="pd-box">
              <Icon name="check" />
            </span>
            <span className="m">
              <span className="t">These are my own clients, who gave me their details. I am not uploading a bought or scraped list.</span>
            </span>
          </button>
        </div>
        {pending && (
          <div className="pn-grp pd-gap">
            <div className="pn-irow col">
              <label htmlFor="pd-list-dial">
                {pending.missing.toLocaleString("en-US")} phone number{pending.missing === 1 ? " has" : "s have"} no country code. They are in:
              </label>
              <select id="pd-list-dial" value={dial} onChange={(e) => setDial(e.target.value)}>
                {DIAL_CODES.map(([c, n, d]) => (
                  <option key={c} value={d}>
                    {n} ({d})
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
        {error && <p className="pd-err pd-errbox">{error}</p>}
        <p className="pn-note pd-priv">
          <Icon name="shield" />
          <span>Scrambled before it goes to Meta, and used only to find matches.</span>
        </p>
        {picker}
      </PhoneSheet>
    );

  return (
    <Sheet open={!!mode} onClose={close} label={title} className="addlist">
      <span className="eyebrow">{mode && mode.kind !== "new" ? mode.list.name : productName}</span>
      <h2>{title}</h2>
      <p className="hint">A spreadsheet with an email or a phone number for each person.</p>
      <div className="drop">
        <span className="ki v">
          <Icon name="upload" />
        </span>
        <b>Choose a spreadsheet</b>
        {mode?.kind === "new" && (
          <label className="fld">
            <span>Name it</span>
            <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="Past clients" />
          </label>
        )}
        <button className={`consent${consent ? " on" : ""}`} type="button" aria-pressed={consent} onClick={() => setConsent((c) => !c)}>
          <span className="box">
            <Icon name="check" />
          </span>
          <span>These are my own clients, who gave me their details. I am not uploading a bought or scraped list.</span>
        </button>
        {pending ? (
          <div className="dial">
            <label className="fld">
              <span>
                {pending.missing.toLocaleString("en-US")} phone number{pending.missing === 1 ? " has" : "s have"} no country code. They are in:
              </span>
              <select value={dial} onChange={(e) => setDial(e.target.value)}>
                {DIAL_CODES.map(([c, n, d]) => (
                  <option key={c} value={d}>
                    {n} ({d})
                  </option>
                ))}
              </select>
            </label>
            <button className="btn" type="button" disabled={busy} onClick={() => void go(pending.rows, dial)}>
              {busy && <span className="spin" />}
              {busy ? "Sending to Meta" : "Continue"}
            </button>
          </div>
        ) : (
          <button className="btn" type="button" disabled={!ready || busy} onClick={() => file.current?.click()}>
            {busy ? <span className="spin" /> : <Icon name="upload" />}
            {busy ? "Sending to Meta" : "Choose the file"}
          </button>
        )}
        {picker}
        {error && <p className="warnl">{error}</p>}
      </div>
      <p className="hint privacy">
        <Icon name="shield" /> Scrambled before it goes to Meta, and used only to find matches.
      </p>
    </Sheet>
  );
}
